import type {
  CatalogBodyGuard,
  CatalogManifest,
  StoredCatalogEntry,
  StoredCatalogManifest,
} from './types'

/** FE cache schema version — bump to drop every client's stored catalog on the next boot. */
const SCHEMA_VERSION = 1

/** `name` is at once the URL segment, the localStorage key and the version key. */
export const CATALOG_KEY_PREFIX = 'orphex.catalog.'
export const CATALOG_MANIFEST_STORAGE_KEY = `${CATALOG_KEY_PREFIX}manifest`

export function catalogStorageKey(name: string): string {
  return `${CATALOG_KEY_PREFIX}${name}`
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function remove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key)
  } catch {
    // Unavailable storage — it is already unusable as a cache.
  }
}

function read<T extends { v: number }>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!record(parsed) || parsed.v !== SCHEMA_VERSION) {
      remove(storage, key)
      return null
    }
    return parsed as T
  } catch {
    // Corrupt JSON or unavailable storage is a cache miss, never a crash.
    remove(storage, key)
    return null
  }
}

function write(storage: Storage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota exceeded / unavailable storage — the cache write is best-effort.
  }
}

/** A manifest entry's `url` is followed on the next boot, so a poisoned cache must not be able
 *  to redirect a catalog read off-origin (`//host/…` is protocol-relative, not a local path).
 *
 *  `version` is accepted as a string (ORP-20196 wire, what staging serves) or a number (the shape
 *  every manifest cached before that change carries, and what any backend behind staging still
 *  emits) — see `CatalogVersion`. Rejecting either shape invalidates those manifests, which
 *  silently drops the cache and costs a full refetch on every boot. */
export function isValidCatalogManifest(body: unknown): body is CatalogManifest {
  if (!record(body) || typeof body.version !== 'string' || !Array.isArray(body.entries))
    return false
  return body.entries.every(
    (entry) =>
      record(entry) &&
      typeof entry.name === 'string' &&
      (typeof entry.version === 'string' || Number.isInteger(entry.version)) &&
      typeof entry.url === 'string' &&
      entry.url.startsWith('/') &&
      !entry.url.startsWith('//'),
  )
}

export function readManifestCache(storage: Storage = localStorage): StoredCatalogManifest | null {
  const entry = read<StoredCatalogManifest>(storage, CATALOG_MANIFEST_STORAGE_KEY)
  if (entry && !isValidCatalogManifest(entry.body)) {
    remove(storage, CATALOG_MANIFEST_STORAGE_KEY)
    return null
  }
  return entry
}

export function writeManifestCache(
  entry: Omit<StoredCatalogManifest, 'v'>,
  storage: Storage = localStorage,
): void {
  write(storage, CATALOG_MANIFEST_STORAGE_KEY, { v: SCHEMA_VERSION, ...entry })
}

/** Records written before `fingerprint` existed read back with `fingerprint: undefined`, which
 *  never matches a manifest version — they revalidate once (304, no re-download) and re-stamp. */
export function readCatalogCache<T>(
  name: string,
  isValidBody: CatalogBodyGuard<T>,
  storage: Storage = localStorage,
): StoredCatalogEntry<T> | null {
  const key = catalogStorageKey(name)
  const entry = read<StoredCatalogEntry<unknown>>(storage, key)
  if (!entry) return null
  if (!isValidBody(entry.body)) {
    remove(storage, key)
    return null
  }
  return entry as StoredCatalogEntry<T>
}

export function writeCatalogCache<T>(
  name: string,
  entry: Omit<StoredCatalogEntry<T>, 'v'>,
  storage: Storage = localStorage,
): void {
  write(storage, catalogStorageKey(name), { v: SCHEMA_VERSION, ...entry })
}

/** Drop one cached body after a write this client knows moved the catalog server-side. The next
 *  read then has no cached copy, so it fetches in full instead of trusting this boot's manifest
 *  fingerprint — which cannot yet know about the change. */
export function invalidateCatalogEntry(name: string, storage: Storage = localStorage): void {
  remove(storage, catalogStorageKey(name))
}

/** Logout teardown: the catalog may become auth-scoped, and stale per-token data is worse than
 *  a re-fetch. Every `orphex.catalog.*` key goes, including entries this build never reads. */
export function clearCatalogCache(storage: Storage = localStorage): void {
  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(CATALOG_KEY_PREFIX)) keys.push(key)
    }
    for (const key of keys) storage.removeItem(key)
  } catch {
    // Unavailable storage — nothing to clear.
  }
}
